import { css } from "./style.ts";

type Position = "bottom-right" | "bottom-left";
type Display = "popup" | "inline";

type FieldType = "text" | "email" | "tel" | "textarea" | "select" | "checkbox" | "consent";
type Field = {
  name: string;
  type: FieldType;
  label?: string;
  placeholder?: string;
  required?: boolean;
  options?: string[];
};
type Step = { title?: string; subtitle?: string; cta?: string; fields: Field[] };

type Config = {
  apiBase: string;
  siteKey: string;
  position: Position;
  display: Display;
  mount: string;
  image: string;
  progressiveCapture: boolean;
  label: string;
  icon: string;
  title: string;
  subtitle: string;
  cta: string;
  successTitle: string;
  successMessage: string;
  doneLabel: string;
  primary: string;
  onPrimary: string;
  font: string;
  radius: string;
  steps: Step[];
};

// Formulaire par défaut (rétrocompat : sites sans `steps` configurés).
const DEFAULT_STEPS: Step[] = [
  {
    fields: [
      { name: "name", type: "text", label: "Nom", placeholder: "Votre nom" },
      { name: "email", type: "email", label: "Email", placeholder: "vous@exemple.com" },
      { name: "phone", type: "tel", label: "Téléphone (optionnel)", placeholder: "+352 …" },
      { name: "message", type: "textarea", label: "Message (optionnel)" },
    ],
  },
];

const DEFAULTS = {
  apiBase: "https://api.gethumanlead.com",
  position: "bottom-right" as Position,
  display: "popup" as Display,
  mount: "",
  image: "",
  progressiveCapture: false,
  label: "Prendre contact",
  icon: "💬",
  title: "On vous recontacte",
  subtitle: "Laissez vos coordonnées, on revient vers vous dans la journée.",
  cta: "Envoyer",
  successTitle: "Merci !",
  successMessage: "On revient vers vous très vite.",
  doneLabel: "Envoyé",
  primary: "#111",
  onPrimary: "#fff",
  font:
    '-apple-system, system-ui, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  radius: "14px",
};

const RESERVED = ["email", "phone", "name", "message"] as const;

// Config serveur (autorité dashboard) : surcharges visuelles + form builder.
type ServerConfig = Partial<{
  position: string;
  display: string;
  mount: string;
  image: string;
  progressiveCapture: boolean;
  label: string;
  icon: string;
  title: string;
  subtitle: string;
  cta: string;
  successTitle: string;
  successMessage: string;
  doneLabel: string;
  primary: string;
  onPrimary: string;
  font: string;
  radius: string;
  steps: Step[];
}>;

type RawAttrs = { siteKey: string; apiBase: string; overrides: ServerConfig };

function normalizePosition(v: string | undefined): Position {
  return v === "bottom-left" ? "bottom-left" : DEFAULTS.position;
}
function normalizeDisplay(v: string | undefined): Display {
  return v === "inline" ? "inline" : DEFAULTS.display;
}

function readAttrs(): RawAttrs | null {
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector<HTMLScriptElement>('script[data-key^="hl_pk_"]');
  if (!script) return null;
  const siteKey = script.dataset.key;
  if (!siteKey) return null;

  const d = script.dataset;
  const overrides: ServerConfig = {};
  const set = (k: keyof ServerConfig, v: string | undefined) => {
    if (v !== undefined) (overrides[k] as string) = v;
  };
  set("label", d.label);
  set("icon", d.icon);
  set("title", d.title);
  set("subtitle", d.subtitle);
  set("cta", d.cta);
  set("successTitle", d.successTitle);
  set("successMessage", d.successMessage);
  set("doneLabel", d.doneLabel);
  set("primary", d.primary);
  set("onPrimary", d.onPrimary);
  set("font", d.font);
  set("radius", d.radius);
  set("mount", d.mount);
  set("image", d.image);
  if (d.position !== undefined) overrides.position = d.position;
  if (d.display !== undefined) overrides.display = d.display;

  return { siteKey, apiBase: d.api ?? DEFAULTS.apiBase, overrides };
}

// --- Config serveur + ciblage par URL ------------------------------------

type Rule = { kind: "show" | "hide"; op: "all" | "contains" | "starts" | "glob"; value: string };
type ServerButton = { id: string | number; config: ServerConfig; rules: Rule[] };

async function fetchButtons(apiBase: string, siteKey: string): Promise<ServerButton[]> {
  try {
    const r = await fetch(`${apiBase}/v1/widget?key=${encodeURIComponent(siteKey)}`);
    const d = (await r.json()) as { ok?: boolean; buttons?: ServerButton[] } | null;
    if (d?.ok && Array.isArray(d.buttons)) return d.buttons;
  } catch {
    // réseau KO : on retombera sur les data-* / défauts
  }
  return [];
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function ruleMatches(rule: Rule, loc: Location): boolean {
  switch (rule.op) {
    case "all":
      return true;
    case "contains":
      return loc.href.includes(rule.value);
    case "starts":
      return loc.pathname.startsWith(rule.value);
    case "glob": {
      const re = new RegExp("^" + rule.value.split("*").map(escapeRegex).join(".*") + "$");
      return re.test(loc.pathname);
    }
    default:
      return false;
  }
}

function buttonEligible(rules: Rule[], loc: Location): boolean {
  const shows = rules.filter((r) => r.kind === "show");
  if (shows.length && !shows.some((r) => ruleMatches(r, loc))) return false;
  if (rules.some((r) => r.kind === "hide" && ruleMatches(r, loc))) return false;
  return true;
}

function pickButton(buttons: ServerButton[], loc: Location): ServerButton | null {
  for (const b of buttons) {
    if (buttonEligible(Array.isArray(b.rules) ? b.rules : [], loc)) return b;
  }
  return null;
}

function buildConfig(attrs: RawAttrs, serverConfig: ServerConfig): Config {
  // Précédence : défauts < data-* (fallback) < config serveur (autorité dashboard).
  const merged = { ...DEFAULTS, ...attrs.overrides, ...serverConfig };
  const steps =
    Array.isArray(serverConfig.steps) && serverConfig.steps.length
      ? serverConfig.steps
      : DEFAULT_STEPS;
  return {
    ...merged,
    siteKey: attrs.siteKey,
    apiBase: attrs.apiBase,
    position: normalizePosition(merged.position),
    display: normalizeDisplay(merged.display),
    progressiveCapture: Boolean(merged.progressiveCapture),
    steps,
  };
}

// --- État "déjà envoyé" (popup) ------------------------------------------

function alreadySent(siteKey: string): boolean {
  try {
    return sessionStorage.getItem(`hl_sent_${siteKey}`) === "1";
  } catch {
    return false;
  }
}
function markSent(siteKey: string): void {
  try {
    sessionStorage.setItem(`hl_sent_${siteKey}`, "1");
  } catch {
    // ignore
  }
}

// --- Helpers DOM ----------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string> = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") node.className = v;
    else node.setAttribute(k, v);
  }
  for (const c of children) {
    node.append(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return node;
}

function isEmail(v: string): boolean {
  return /.+@.+\..+/.test(v);
}

// --- Pastille flottante (popup) ------------------------------------------

function buildFab(config: Config, onClick: () => void): HTMLButtonElement {
  const btn = el(
    "button",
    { class: "fab", type: "button", "aria-label": config.label },
    [el("span", { class: "ico" }, [config.icon]), el("span", {}, [config.label])],
  );
  btn.addEventListener("click", onClick);
  return btn;
}

function buildDoneFab(config: Config): HTMLButtonElement {
  return el(
    "button",
    { class: "fab done", type: "button", "aria-label": config.doneLabel, disabled: "true" },
    [el("span", { class: "ico" }, ["✓"]), el("span", {}, [config.doneLabel])],
  );
}

// --- Moteur de formulaire multi-étapes -----------------------------------

type FieldControl = {
  field: Field;
  input: HTMLElement;
  read: () => unknown;
  valid: () => boolean;
};

function splitData(data: Record<string, unknown>): {
  top: Record<string, string>;
  payload: Record<string, unknown>;
} {
  const top: Record<string, string> = {};
  const payload: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if ((RESERVED as readonly string[]).includes(k)) {
      if (typeof v === "string" && v.trim()) top[k] = v.trim();
    } else {
      payload[k] = v;
    }
  }
  return { top, payload };
}

function buildForm(
  config: Config,
  opts: { onClose?: () => void; onDone: () => void },
): HTMLElement {
  const panel = el("div", { class: "panel", role: "dialog", "aria-modal": "false" });

  const head = el("div", { class: "head" });
  if (opts.onClose) {
    const closeBtn = el("button", { class: "close", "aria-label": "Fermer", type: "button" }, ["×"]);
    closeBtn.addEventListener("click", opts.onClose);
    head.append(closeBtn);
  }
  if (config.image) head.append(el("img", { class: "hero", src: config.image, alt: "" }));
  const titleEl = el("h2", {}, [config.title]);
  const subEl = el("p", { class: "sub" }, [config.subtitle]);
  head.append(titleEl, subEl);

  const body = el("div", { class: "body" });
  const progress = el("div", { class: "progress" });
  const stepWrap = el("div", { class: "step" });
  const errBox = el("div", { class: "err", role: "alert", style: "display:none" });
  const nav = el("div", { class: "nav" });
  const backBtn = el("button", { class: "back", type: "button" }, ["Retour"]);
  const nextBtn = el("button", { class: "submit", type: "button" }, ["Continuer"]);
  nav.append(backBtn, nextBtn);
  body.append(progress, stepWrap, errBox, nav);

  const footer = el("div", { class: "footer" }, [
    "Anti-spam par ",
    el("a", { href: "https://gethumanlead.com", target: "_blank", rel: "noopener" }, ["gethumanlead"]),
  ]);

  panel.append(head, body, footer);

  const steps = config.steps;
  const multi = steps.length > 1;
  const data: Record<string, unknown> = {};
  let current = 0;
  let leadId: string | number | null = null;
  let token: string | null = null;
  let controls: FieldControl[] = [];

  const hasContact = () =>
    Boolean(
      (typeof data.email === "string" && data.email.trim()) ||
        (typeof data.phone === "string" && data.phone.trim()),
    );

  function fieldControl(f: Field): FieldControl {
    if (f.type === "checkbox" || f.type === "consent") {
      const input = el("input", { type: "checkbox" }) as HTMLInputElement;
      if (data[f.name] === true) input.checked = true;
      const wrap = el("label", { class: "check" }, [input, el("span", {}, [f.label ?? f.name])]);
      stepWrap.append(wrap);
      return {
        field: f,
        input,
        read: () => input.checked,
        valid: () => (f.required || f.type === "consent" ? input.checked : true),
      };
    }
    if (f.type === "select") {
      const sel = document.createElement("select");
      sel.append(el("option", { value: "" }, [f.placeholder ?? "Choisir…"]));
      for (const o of f.options ?? []) sel.append(el("option", { value: o }, [o]));
      if (typeof data[f.name] === "string") sel.value = data[f.name] as string;
      const wrap = el("label", {});
      if (f.label) wrap.append(document.createTextNode(f.label));
      wrap.append(sel);
      stepWrap.append(wrap);
      return {
        field: f,
        input: sel,
        read: () => sel.value.trim(),
        valid: () => (f.required ? sel.value.trim() !== "" : true),
      };
    }
    const input =
      f.type === "textarea"
        ? document.createElement("textarea")
        : document.createElement("input");
    if (input instanceof HTMLInputElement) input.type = f.type;
    if (f.placeholder) input.placeholder = f.placeholder;
    if (typeof data[f.name] === "string") input.value = data[f.name] as string;
    const wrap = el("label", {});
    if (f.label) wrap.append(document.createTextNode(f.label));
    wrap.append(input);
    stepWrap.append(wrap);
    return {
      field: f,
      input,
      read: () => input.value.trim(),
      valid: () => {
        const v = input.value.trim();
        if (f.required && !v) return false;
        if (f.type === "email" && v && !isEmail(v)) return false;
        return true;
      },
    };
  }

  function renderStep() {
    errBox.style.display = "none";
    stepWrap.replaceChildren();
    controls = [];
    const step = steps[current];
    titleEl.textContent = step.title ?? config.title;
    const sub = step.subtitle ?? (current === 0 ? config.subtitle : "");
    subEl.textContent = sub;
    subEl.style.display = sub ? "" : "none";
    for (const f of step.fields) controls.push(fieldControl(f));
    const isLast = current === steps.length - 1;
    backBtn.style.display = current > 0 ? "" : "none";
    nextBtn.textContent = step.cta ?? (isLast ? config.cta : "Continuer");
    progress.textContent = multi ? `Étape ${current + 1} / ${steps.length}` : "";
    progress.style.display = multi ? "" : "none";
  }

  // Lit + valide l'étape courante, recopie les valeurs dans `data`.
  function collectStep(): boolean {
    let ok = true;
    for (const c of controls) {
      const good = c.valid();
      c.input.classList.toggle("invalid", !good);
      if (!good) ok = false;
      data[c.field.name] = c.read();
    }
    if (!ok) {
      errBox.textContent = "Merci de compléter les champs requis.";
      errBox.style.display = "block";
    } else {
      errBox.style.display = "none";
    }
    return ok;
  }

  async function postJson(url: string, payload: unknown): Promise<Record<string, unknown>> {
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const d = (await r.json().catch(() => null)) as Record<string, unknown> | null;
    if (!r.ok || !d?.ok) throw new Error((d?.error as string) ?? "request_failed");
    return d;
  }

  async function createLead(partial: boolean): Promise<void> {
    const { top, payload } = splitData(data);
    const d = await postJson(`${config.apiBase}/v1/leads`, {
      site_key: config.siteKey,
      ...top,
      payload,
      source_url: location.href,
      partial: partial || undefined,
    });
    if (partial) {
      const lead = d.lead as { id: string | number } | undefined;
      leadId = lead?.id ?? null;
      token = (d.token as string) ?? null;
    }
  }

  async function appendLead(complete: boolean): Promise<void> {
    const { top, payload } = splitData(data);
    await postJson(`${config.apiBase}/v1/leads/${leadId}/append`, {
      site_key: config.siteKey,
      token,
      ...top,
      payload,
      complete: complete || undefined,
    });
  }

  function showSuccess() {
    markSent(config.siteKey);
    const ok = el("div", { class: "ok" }, [
      el("div", { class: "check" }, ["✓"]),
      el("h3", {}, [config.successTitle]),
      el("p", {}, [config.successMessage]),
    ]);
    panel.replaceChildren(head, ok, footer);
    head.querySelector(".close")?.remove();
    opts.onDone();
  }

  async function finalize(): Promise<void> {
    if (leadId) {
      await appendLead(true);
      return;
    }
    if (!hasContact()) {
      errBox.textContent = "Email ou téléphone requis.";
      errBox.style.display = "block";
      throw new Error("email_or_phone_required");
    }
    await createLead(false);
  }

  async function onNext() {
    if (!collectStep()) return;
    const isLast = current === steps.length - 1;
    const label = nextBtn.textContent ?? "";
    nextBtn.disabled = true;
    backBtn.disabled = true;
    nextBtn.textContent = "…";
    try {
      if (isLast) {
        await finalize();
        showSuccess();
        return;
      }
      // Étape intermédiaire : commit progressif si activé et contact déjà saisi.
      if (config.progressiveCapture) {
        if (!leadId && hasContact()) await createLead(true);
        else if (leadId) await appendLead(false);
      }
      current += 1;
      renderStep(); // pose le label de la nouvelle étape — ne pas l'écraser ensuite
    } catch (e) {
      if ((e as Error).message !== "email_or_phone_required") {
        errBox.textContent = "Une erreur est survenue. Réessayez.";
        errBox.style.display = "block";
      }
      nextBtn.textContent = label; // restaure uniquement en cas d'échec
    } finally {
      nextBtn.disabled = false;
      backBtn.disabled = false;
    }
  }

  nextBtn.addEventListener("click", onNext);
  backBtn.addEventListener("click", () => {
    if (current > 0) {
      current -= 1;
      renderStep();
    }
  });

  renderStep();
  return panel;
}

// --- Montage --------------------------------------------------------------

function applyVars(container: HTMLElement, config: Config): void {
  container.style.setProperty("--hl-primary", config.primary);
  container.style.setProperty("--hl-on-primary", config.onPrimary);
  container.style.setProperty("--hl-font", config.font);
  container.style.setProperty("--hl-radius", config.radius);
}

function mount(config: Config): void {
  document.querySelectorAll("[data-hl-widget]").forEach((n) => n.remove());

  let inline = config.display === "inline";
  let mountEl: Element | null = null;
  if (inline) {
    mountEl = config.mount ? document.querySelector(config.mount) : null;
    if (!mountEl) inline = false; // cible introuvable → repli en popup
  }

  const host = document.createElement("div");
  host.dataset.hlWidget = "1";
  const root = host.attachShadow({ mode: "closed" });
  (inline && mountEl ? mountEl : document.body).appendChild(host);

  const style = document.createElement("style");
  style.textContent = css;
  root.appendChild(style);

  const cls = inline ? "inline" : config.position === "bottom-left" ? "bl" : "br";
  const container = el("div", { class: `root ${cls}` });
  applyVars(container, config);
  root.appendChild(container);

  if (inline) {
    container.appendChild(buildForm(config, { onDone: () => {} }));
    return;
  }

  let sent = alreadySent(config.siteKey);
  const renderFab = () => {
    container.replaceChildren(sent ? buildDoneFab(config) : buildFab(config, openPanel));
  };
  const closePanel = () => renderFab();
  function openPanel() {
    container.replaceChildren(
      buildForm(config, {
        onClose: closePanel,
        onDone: () => {
          sent = true;
        },
      }),
    );
  }
  renderFab();
}

function mountWhenReady(config: Config): void {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mount(config), { once: true });
  } else {
    mount(config);
  }
}

(async function init() {
  const attrs = readAttrs();
  if (!attrs) {
    console.warn("[humanlead] missing data-key");
    return;
  }

  const buttons = await fetchButtons(attrs.apiBase, attrs.siteKey);
  let serverConfig: ServerConfig = {};
  if (buttons.length > 0) {
    const picked = pickButton(buttons, location);
    if (!picked) return; // des boutons existent mais aucun ne cible cette page
    serverConfig = picked.config ?? {};
  }

  mountWhenReady(buildConfig(attrs, serverConfig));
})();
